import React, { useState, useEffect, useRef, useCallback } from 'react';
import useReducedMotion from '../../hooks/useReducedMotion';

export default function MagneticText({ text, className, delay = 0 }) {
  const containerRef = useRef(null);
  const charsRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const positionsRef = useRef([]);
  const rafRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    positionsRef.current = text.split('').map(() => ({ x: 0, y: 0, vx: 0, vy: 0, rot: 0, vrot: 0 }));
  }, [text]);

  const animate = useCallback(() => {
    const chars = charsRef.current;

    chars.forEach((charEl, i) => {
      if (!charEl || text[i] === ' ') return;

      const pos = positionsRef.current[i];
      const rect = charEl.getBoundingClientRect();
      const charCenterX = rect.left + rect.width / 2;
      const charCenterY = rect.top + rect.height / 2;

      const dx = mouseRef.current.x - charCenterX;
      const dy = mouseRef.current.y - charCenterY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const maxDistance = 120;

      if (distance < maxDistance) {
        const force = (1 - distance / maxDistance) * 18;
        const angle = Math.atan2(dy, dx);
        pos.vx -= Math.cos(angle) * force * 0.15;
        pos.vy -= Math.sin(angle) * force * 0.15;

        // Rotation based on horizontal push direction
        const rotForce = (1 - distance / maxDistance) * 25;
        pos.vrot += (dx > 0 ? -1 : 1) * rotForce * 0.15;
      }

      // Spring back to origin
      pos.vx += -pos.x * 0.04;
      pos.vy += -pos.y * 0.04;

      // Spring rotation back to 0
      pos.vrot += -pos.rot * 0.06;

      // Damping
      pos.vx *= 0.85;
      pos.vy *= 0.85;
      pos.vrot *= 0.85;

      pos.x += pos.vx;
      pos.y += pos.vy;
      pos.rot += pos.vrot;

      charEl.style.transform = `translate(${pos.x}px, ${pos.y}px) rotate(${pos.rot}deg)`;
    });

    rafRef.current = requestAnimationFrame(animate);
  }, [text]);

  useEffect(() => {
    if (!visible || reducedMotion) return;

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      mouseRef.current = { x: touch.clientX, y: touch.clientY };
    };
    const handleTouchEnd = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible, animate, reducedMotion]);

  if (!visible) {
    return <span className={className} style={{ visibility: 'hidden' }}>{text}</span>;
  }

  // Reduced motion: render plain text without per-character spans
  if (reducedMotion) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span ref={containerRef} className={className} aria-label={text}>
      {text.split('').map((char, i) => (
        <span
          key={i}
          ref={(el) => (charsRef.current[i] = el)}
          style={{
            display: 'inline-block',
            whiteSpace: char === ' ' ? 'pre' : 'normal',
            willChange: 'transform',
          }}
        >
          {char}
        </span>
      ))}
    </span>
  );
}
